const STORAGE_KEY = 'lv_arschkarte_v1';

const ARSCHKARTE = {
  id: 'arschkarte',
  icon: '<img src="/assets/img/arschgesicht.png" alt="" loading="lazy">',
  name: 'Die Arschkarte',
  text: 'Pech gehabt.',
};

const ITEMS = [
  { id: 'stolperstein', icon: '🪨', name: 'Stolperstein', text: 'Perfekt für den Weg nach vorn. Oder zum Hinfallen.' },
  { id: 'gummistiefel', icon: '🥾', name: 'Linker Gummistiefel', text: 'Der rechte ist leider weg.' },
  { id: 'wuerstchen', icon: '🌭', name: 'Lauwarmes Würstchen', text: 'Seit 2019 im Angebot.' },
  { id: 'kaktus', icon: '🌵', name: 'Kaktus mit Charakter', text: 'Stachelig, aber pflegeleicht.' },
  { id: 'socke', icon: '🧦', name: 'Einzelne Socke', text: 'Die Waschmaschine hat den Rest.' },
  { id: 'gutschein', icon: '🎟️', name: 'Abgelaufener Gutschein', text: 'Gültig bis gestern.' },
  { id: 'regenschirm', icon: '☂️', name: 'Kaputter Regenschirm', text: 'Hält exakt bei Sonnenschein.' },
  { id: 'glitzer', icon: '✨', name: 'Eine Prise Glitzer', text: 'Du wirst ihn nie wieder los.' },
  { id: 'banane', icon: '🍌', name: 'Bananenschale', text: 'Bitte nicht auf den Boden werfen.' },
  { id: 'wecker', icon: '⏰', name: 'Wecker ohne Snooze', text: 'Gnadenlos pünktlich.' },
  { id: 'rubberduck', icon: '🦆', name: 'Quietscheente', text: 'Hört dir immer zu.' },
  { id: 'zahnstocher', icon: '🪥', name: 'Gebrauchte Zahnbürste', text: 'Bitte nicht fragen, wessen.' },
  { id: 'fernbedienung', icon: '📺', name: 'Fernbedienung ohne Batterien', text: 'Aufstehen ist Pflicht.' },
  { id: 'toast', icon: '🍞', name: 'Toast (Butterseite unten)', text: 'Naturgesetz.' },
];

const STOPPERS = [
  'Du hast die Arschkarte gezogen',
  'Herzlichen Glückwunsch',
  'Versuch es morgen nochmal',
];

const todayKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const shuffle = (list) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const defaultState = () => ({
  day: todayKey(),
  over: false,
  items: [],
  round: null,
  picked: null,
  // Lifetime data, kept for the upcoming statistics and leaderboard.
  stats: {
    gamesPlayed: 0,
    arschkarten: 0,
    totalItems: 0,
    bestRun: 0,
    itemCounts: {},
    history: [],
  },
});

class ArschkarteGame extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;
    this.attachShadow({ mode: 'open' });
    this.state = this.load();
    this.rollOverDay();
    this.ensureRound();
    this.render();
  }

  load() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (raw && typeof raw === 'object') {
        return { ...defaultState(), ...raw, stats: { ...defaultState().stats, ...(raw.stats || {}) } };
      }
    } catch (e) {
      // Ignore corrupted or unavailable storage.
    }
    return defaultState();
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      // Storage may be disabled; the game still works for this session.
    }
  }

  rollOverDay() {
    if (this.state.day !== todayKey()) {
      this.state.day = todayKey();
      // The account is kept across days; only an Arschkarte ends the current run.
      if (this.state.over) this.state.items = [];
      this.state.over = false;
      this.state.round = null;
      this.state.picked = null;
      this.save();
    }
  }

  ensureRound() {
    if (this.state.round) return;
    const item = shuffle(ITEMS)[0].id;
    this.state.round = shuffle([ARSCHKARTE.id, ARSCHKARTE.id, item]);
    this.state.picked = null;
    this.save();
  }

  find(id) {
    return id === ARSCHKARTE.id ? ARSCHKARTE : ITEMS.find((i) => i.id === id);
  }

  pick(index) {
    if (this.state.picked !== null) return;

    const card = this.find(this.state.round[index]);
    this.state.picked = index;

    if (card.id === ARSCHKARTE.id) {
      this.finishGame();
    } else {
      this.state.items.push(card.id);
      const s = this.state.stats;
      s.totalItems += 1;
      s.itemCounts[card.id] = (s.itemCounts[card.id] || 0) + 1;
    }

    this.save();
    this.render();
  }

  finishGame() {
    const s = this.state.stats;
    this.state.over = true;
    s.gamesPlayed += 1;
    s.arschkarten += 1;
    s.bestRun = Math.max(s.bestRun, this.state.items.length);
    s.history.push({ day: this.state.day, score: this.state.items.length, items: [...this.state.items] });
    s.history = s.history.slice(-100);
  }

  reset() {
    if (!confirm('Alle Spielstände auf diesem Gerät löschen?')) return;
    this.state = defaultState();
    this.ensureRound();
    this.render();
  }

  render() {
    const { round, picked, over, items } = this.state;
    const revealed = picked !== null;

    const cards = (round || [])
      .map((id, i) => {
        const card = this.find(id);
        const classes = ['card'];
        if (i === picked) classes.push('flipped', 'chosen');
        if (id === ARSCHKARTE.id && revealed) classes.push('bad');
        return `
          <button class="${classes.join(' ')}" data-index="${i}" ${revealed ? 'disabled' : ''}
                  aria-label="Karte ${i + 1}${i === picked ? ': ' + card.name : ' ziehen'}">
            <span class="inner">
              <span class="face back">?</span>
              <span class="face front">
                <span class="icon">${card.icon}</span>
                <strong>${card.name}</strong>
                <small>${card.text}</small>
              </span>
            </span>
          </button>`;
      })
      .join('');

    let message = '';
    if (over) {
      message = `
        <div class="message lost" role="status">
          ${STOPPERS.map((t) => `<p>${t}</p>`).join('')}
          <p class="score">Dein Ergebnis: ${items.length} ${items.length === 1 ? 'Gegenstand' : 'Gegenstände'}</p>
        </div>`;
    } else if (revealed) {
      const card = this.find(round[picked]);
      message = `
        <div class="message won" role="status">
          <p>Du hast <strong>${card.name}</strong> gezogen und deinem Konto hinzugefügt.</p>
          <p class="hint">Für heute war's das. Komm morgen wieder.</p>
        </div>`;
    } else {
      message = '<p class="hint">Zieh eine Karte. Zwei davon sind die Arschkarte.</p>';
    }

    const inventory = items.length
      ? `<ul class="inventory">${items
        .map((id) => `<li title="${this.find(id).name}">${this.find(id).icon} ${this.find(id).name}</li>`)
        .join('')}</ul>`
      : '<p class="empty">Noch nichts gezogen.</p>';

    const s = this.state.stats;

    this.shadowRoot.innerHTML = `
      <style>${ArschkarteGame.styles}</style>
      <section class="game">
        <div class="cards">${cards}</div>
        ${message}
        <div class="account">
          <h3>Dein Spielerkonto</h3>
          ${inventory}
          <p class="meta">
            Spiele: ${s.gamesPlayed} · Bester Lauf: ${s.bestRun} · Gesamt gezogen: ${s.totalItems}
          </p>
          <button class="link" data-action="reset">Spielstand zurücksetzen</button>
        </div>
      </section>`;

    this.shadowRoot.querySelectorAll('.card').forEach((el) => {
      el.addEventListener('click', () => this.pick(Number(el.dataset.index)));
    });
    this.shadowRoot.querySelector('[data-action="reset"]')?.addEventListener('click', () => this.reset());
  }

  static styles = `
    :host { display: block; }
    * { box-sizing: border-box; }
    .game { max-width: 760px; margin: 0 auto; text-align: center; }
    .cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; perspective: 1000px; }
    .card { all: unset; cursor: pointer; display: block; aspect-ratio: 3 / 4; }
    .card:disabled { cursor: default; }
    .card:focus-visible { outline: 3px solid #1ca7b7; outline-offset: 4px; border-radius: 12px; }
    .inner { position: relative; display: block; width: 100%; height: 100%;
      transition: transform .6s; transform-style: preserve-3d; }
    .card.flipped .inner { transform: rotateY(180deg); }
    .card:not(:disabled):hover .inner { transform: translateY(-6px); }
    .face { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: .4rem; padding: .75rem; border-radius: 12px;
      backface-visibility: hidden; border: 2px solid #155f6c; }
    .back { background: repeating-linear-gradient(45deg, #1ca7b7, #1ca7b7 10px, #155f6c 10px, #155f6c 20px);
      color: #fff; font-size: 3rem; font-weight: 700; }
    .front { background: #fff; transform: rotateY(180deg); color: #222; }
    .icon { font-size: 2.5rem; }
    .icon img { display: block; width: 100%; max-width: 110px; height: auto; }
    .front small { color: #555; font-size: .8rem; line-height: 1.3; }
    .card.flipped:not(.chosen) { opacity: .55; }
    .card.bad .front { background: #ffe3e0; border-color: #c0392b; }
    .message { margin: 1.5rem 0 1rem; }
    .message p { margin: .25rem 0; }
    .lost p:not(.score) { font-size: 1.4rem; font-weight: 700; color: #c0392b; }
    .score, .hint, .meta, .empty { color: #555; }
    .action { cursor: pointer; border: 0; border-radius: 8px; padding: .7rem 1.4rem; margin-top: .5rem;
      background: #1ca7b7; color: #fff; font-size: 1rem; font-weight: 600; }
    .action:hover { background: #155f6c; }
    .account { margin-top: 2rem; padding: 1rem; background: #f5f5f9; border-radius: 12px; }
    .account h3 { margin: 0 0 .75rem; }
    .inventory { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: .5rem;
      justify-content: center; }
    .inventory li { background: #fff; border: 1px solid #d0d0d0; border-radius: 999px; padding: .25rem .75rem;
      font-size: .9rem; }
    .meta { font-size: .85rem; }
    .link { all: unset; cursor: pointer; font-size: .8rem; color: #777; text-decoration: underline; }
    @media (max-width: 520px) {
      .cards { gap: .5rem; }
      .front small { display: none; }
      .icon { font-size: 2rem; }
      .icon img { max-width: 70px; }
      .front strong { font-size: .8rem; }
    }
    @media (prefers-reduced-motion: reduce) { .inner { transition: none; } }
  `;
}

customElements.define('arschkarte-game', ArschkarteGame);
